import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { checkerboardSx, ILightboxImage, urlFileInfo } from '../../lib/printFileJobs/ui';

type Backdrop = 'checker' | 'white' | 'black';
type Fit = 'fit' | 'actual';

interface Props {
    images: ILightboxImage[];
    index: number | null;
    onClose: () => void;
    onIndexChange: (index: number) => void;
}

/**
 * Full-size viewer for one image out of a group, with prev/next navigation,
 * a switchable backdrop (checkerboard / white / black — useful for transparent
 * print files) and fit / 100% zoom. Shows the natural pixel size once loaded.
 */
export default function ImageLightbox({ images, index, onClose, onIndexChange }: Props) {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const [backdrop, setBackdrop] = useState<Backdrop>('checker');
    const [fit, setFit] = useState<Fit>('fit');
    const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
    const [failed, setFailed] = useState(false);
    const [copied, setCopied] = useState(false);

    const open = index !== null && index >= 0 && index < images.length;
    const image = open ? images[index as number] : null;

    useEffect(() => {
        setNatural(null);
        setFailed(false);
        setCopied(false);
    }, [image?.url]);

    const goPrev = () => {
        if (index === null || images.length < 2) return;
        onIndexChange((index - 1 + images.length) % images.length);
    };
    const goNext = () => {
        if (index === null || images.length < 2) return;
        onIndexChange((index + 1) % images.length);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            goPrev();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            goNext();
        }
    };

    const handleCopy = async () => {
        if (!image) return;
        try {
            await navigator.clipboard.writeText(image.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            /* clipboard unavailable */
        }
    };

    const backdropSx =
        backdrop === 'checker'
            ? checkerboardSx(mode)
            : { backgroundColor: backdrop === 'white' ? '#ffffff' : '#000000' };

    const file = urlFileInfo(image?.url);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            onKeyDown={handleKeyDown}
            PaperProps={{
                sx: {
                    width: '92vw',
                    height: '92vh',
                    maxWidth: '1600px',
                    m: 0,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: theme.palette.background.paper,
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {image?.title ?? ''}
                        {images.length > 1 && index !== null ? (
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                                {index + 1} / {images.length}
                            </Typography>
                        ) : null}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', wordBreak: 'break-all' }}>
                        {image?.subtitle ? `${image.subtitle} · ` : ''}
                        {file.name}
                        {natural ? ` · ${natural.w} × ${natural.h} px` : ''}
                        {file.ext ? ` · ${file.ext.toUpperCase()}` : ''}
                    </Typography>
                </Box>

                <ToggleButtonGroup size="small" exclusive value={backdrop} onChange={(_, v) => v && setBackdrop(v)} aria-label="Backdrop">
                    <ToggleButton value="checker" aria-label="Checkerboard">Checker</ToggleButton>
                    <ToggleButton value="white" aria-label="White">White</ToggleButton>
                    <ToggleButton value="black" aria-label="Black">Black</ToggleButton>
                </ToggleButtonGroup>

                <ToggleButtonGroup size="small" exclusive value={fit} onChange={(_, v) => v && setFit(v)} aria-label="Zoom">
                    <ToggleButton value="fit" aria-label="Fit to window">Fit</ToggleButton>
                    <ToggleButton value="actual" aria-label="Actual size">100%</ToggleButton>
                </ToggleButtonGroup>

                <Tooltip title={copied ? 'Copied' : 'Copy URL'}>
                    <IconButton size="small" onClick={handleCopy} aria-label="Copy URL">
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Open in new tab">
                    <IconButton
                        size="small"
                        component="a"
                        href={image?.url ?? '#'}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Open in new tab"
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto', ...backdropSx }}>
                {images.length > 1 && (
                    <>
                        <IconButton
                            onClick={goPrev}
                            aria-label="Previous image"
                            sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' }, zIndex: 1 }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            onClick={goNext}
                            aria-label="Next image"
                            sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.45)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' }, zIndex: 1 }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </>
                )}

                {image && !failed && (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: fit === 'fit' ? 'flex' : 'block',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: fit === 'fit' ? 2 : 0,
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box
                            component="img"
                            key={image.url}
                            src={image.url}
                            alt={image.title}
                            onLoad={(e: React.SyntheticEvent<HTMLImageElement>) =>
                                setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
                            }
                            onError={() => setFailed(true)}
                            sx={
                                fit === 'fit'
                                    ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }
                                    : { display: 'block', m: 2 }
                            }
                        />
                    </Box>
                )}

                {image && failed && (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d03b3b', textAlign: 'center', p: 3 }}>
                        <Typography variant="body2">
                            Image failed to load.
                            <br />
                            <a href={image.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{image.url}</a>
                        </Typography>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
}
